import { useEffect, useMemo, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import { cn, showToast } from '@my/ui';
import {
  resolveVatTreatmentLine,
  vatTreatmentLineLabel,
  type Address,
  type Organisation,
  type OrganisationAccountingCategory,
  type OrganisationNominal,
  type OrganisationTaxRate,
  type Party,
  type VatTreatmentMatrix,
} from '@my/directory-api';
import { useConsumeOrganisationSequence } from '@my/directory-api/hooks';
import { tmsApi, useGenerateInvoice, type Invoice, type VatRoundingMethodValue } from '@my/tms-api';
import { computeDueDate, describePaymentTerms, resolvePaymentTerms } from '@/utils/paymentTerms';
import { DocumentPdf } from '@/features/settings/documents/render/DocumentPdf';
import { PdfPreviewPane } from '@/features/settings/documents/builder/PdfPreviewPane';
import { useInvoiceDocumentSource } from '../lib/useInvoiceDocumentSource';
import { ForeignVatNotice } from './ForeignVatNotice';

type Mode = 'send' | 'draft' | 'proforma';

type Line = {
  key: string;
  description: string;
  net: number;
  nominal: string;
  category: string;
  rateId: string;
};

/**
 * An invoice that bills no order — storage, a one-off fee — raised from the
 * Create tab's "+ Adhoc invoice". Everything after raising is the ordinary
 * invoice: numbering, the stored PDF, email, cancel, credit notes, draft edit.
 *
 * <p>
 * VAT defaults through the VAT Treatment matrix like any invoice. There is no
 * route, so: a customer in another EU country or outside the area falls on a
 * line where the route does not matter (reverse charge / outside scope); a
 * customer at home is taken as DOMESTIC — the standard line. The dropdown
 * stays editable; with no order-based proposal behind it, changing it is not
 * an "override" (the invoice's History still records the document).
 * </p>
 */
export function AdhocInvoiceBuilder({
  orgId,
  customers,
  addressById,
  organisation,
  taxRates,
  vatMatrix,
  salesNominals,
  accountingCategories,
  onClose,
}: {
  orgId: string;
  customers: Party[];
  addressById: Map<string, Address>;
  organisation: Organisation | undefined;
  taxRates: OrganisationTaxRate[];
  vatMatrix: VatTreatmentMatrix | undefined;
  salesNominals: OrganisationNominal[];
  /** Null when the organisation does not use categories. */
  accountingCategories: OrganisationAccountingCategory[] | null;
  onClose: () => void;
}) {
  const consumeSeq = useConsumeOrganisationSequence();
  const generate = useGenerateInvoice();
  const docSource = useInvoiceDocumentSource(orgId || null);
  const roundingMethod: VatRoundingMethodValue = organisation?.vatRoundingMethod ?? 'Total';

  // ── Customer ──────────────────────────────────────────────────────────
  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const customer = customers.find((c) => c.id === customerId);
  const sortedCustomers = useMemo(
    () =>
      // Every customer, suspended or ceased included — a final invoice is
      // often exactly what a ceased customer needs.
      customers
        .filter((c) => {
          const q = customerSearch.trim().toLowerCase();
          return !q || c.name.toLowerCase().includes(q) || (c.code ?? '').toLowerCase().includes(q);
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [customers, customerSearch],
  );
  const currency = customer?.currencyIsoCode || organisation?.currencyIsoCode || 'EUR';
  const billingAddress = customer?.billingAddressId
    ? (addressById.get(customer.billingAddressId)?.fullAddress ?? null)
    : null;

  // ── VAT default: the matrix, taking a home customer as domestic ──────
  const activeRates = useMemo(
    () =>
      taxRates
        .filter((r) => r.status === 'Active' && r.invoiceType !== 'Purchases')
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [taxRates],
  );
  const treatment = useMemo(() => {
    const home = vatMatrix?.homeCountryIsoCode;
    return resolveVatTreatmentLine(vatMatrix, customer?.taxSchemeCountryCode, home, home);
  }, [vatMatrix, customer?.taxSchemeCountryCode]);
  const defaultRateId =
    (treatment?.taxRateId && activeRates.some((r) => r.id === treatment.taxRateId) ? treatment.taxRateId : null) ??
    activeRates.find((r) => r.taxType === 'Standard')?.id ??
    '';

  // ── Lines ─────────────────────────────────────────────────────────────
  const newLine = (): Line => ({
    key: crypto.randomUUID(),
    description: '',
    net: 0,
    nominal: '',
    category: '',
    rateId: defaultRateId,
  });
  const [lines, setLines] = useState<Line[]>(() => [newLine()]);
  // Choosing (or changing) the customer re-defaults the VAT of lines nobody
  // has touched yet.
  const [rateTouched, setRateTouched] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    setLines((prev) => prev.map((l) => (rateTouched.has(l.key) ? l : { ...l, rateId: defaultRateId })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRateId]);
  const patch = (key: string, p: Partial<Line>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...p } : l)));

  const rateById = useMemo(() => new Map(taxRates.map((r) => [r.id, r])), [taxRates]);
  const pctOf = (id: string) => rateById.get(id)?.taxPercentage ?? 0;
  const subtotal = round2(lines.reduce((s, l) => s + l.net, 0));
  const vatTotal = (() => {
    if (roundingMethod === 'Item') return round2(lines.reduce((s, l) => s + round2((l.net * pctOf(l.rateId)) / 100), 0));
    const byRate = new Map<number, number>();
    for (const l of lines) byRate.set(pctOf(l.rateId), (byRate.get(pctOf(l.rateId)) ?? 0) + l.net);
    return round2([...byRate.entries()].reduce((s, [rate, net]) => s + round2((net * rate) / 100), 0));
  })();

  // ── Header ────────────────────────────────────────────────────────────
  const terms = useMemo(() => resolvePaymentTerms(customer, organisation), [customer, organisation]);
  const termsWording = describePaymentTerms(terms);
  const [issueDate, setIssueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(() => computeDueDate(new Date().toISOString().slice(0, 10), terms));
  const [dueDateEdited, setDueDateEdited] = useState(false);
  useEffect(() => {
    if (!dueDateEdited && issueDate) setDueDate(computeDueDate(issueDate, terms));
  }, [terms, issueDate, dueDateEdited]);
  const [po, setPo] = useState('');
  const [message, setMessage] = useState('Thank you for your business.');
  const [emailMessage, setEmailMessage] = useState('');
  const [mode, setMode] = useState<Mode>('send');

  // ── Checks: the same basics as any invoice ────────────────────────────
  const problems = [
    ...(customer ? [] : ['Choose a customer.']),
    ...lines.flatMap((l, i) => {
      const gaps = [
        ...(l.description.trim() ? [] : ['description']),
        ...(l.rateId ? [] : ['VAT rate']),
        ...(l.nominal ? [] : ['nominal']),
        ...(accountingCategories && !l.category ? ['category'] : []),
      ];
      return gaps.length ? [`Line ${i + 1}: ${gaps.join(' & ')}`] : [];
    }),
    ...(lines.length === 0 ? ['Add at least one line.'] : []),
  ];

  // ── Live preview: the same buildData the raised invoice uses ──────────
  const previewTemplate = docSource.templateFor(mode === 'proforma' ? 'Proforma' : 'Adhoc', customer);
  const previewData = useMemo(() => {
    const draft = {
      id: 'preview',
      number: 'To be allocated',
      docType: mode === 'proforma' ? 'Proforma' : 'Adhoc',
      status: 'Draft',
      customerPartyId: customer?.id ?? '',
      customerName: customer?.name ?? '',
      customerCode: customer?.code ?? null,
      billingName: customer?.billingName ?? null,
      billingAddress,
      customerTaxNumber: customer?.taxNumber?.trim() || null,
      customerTaxCountry: customer?.taxSchemeCountryCode ?? null,
      currencyIsoCode: currency,
      issueDate: issueDate || null,
      dueDate: dueDate || null,
      paymentTerms: termsWording,
      poNumber: po.trim() || null,
      message: message.trim() || null,
      subtotal,
      vatTotal,
      total: subtotal + vatTotal,
      exportStatus: 'Pending',
      isOverdue: false,
      lines: lines
        .filter((l) => l.description.trim())
        .map((l, i) => {
          const r = rateById.get(l.rateId);
          return {
            id: l.key,
            description: l.description.trim(),
            type: 'Custom',
            net: l.net,
            taxRatePct: r?.taxPercentage ?? 0,
            taxAmount: round2((l.net * (r?.taxPercentage ?? 0)) / 100),
            taxRateId: r?.id ?? null,
            taxRateName: r?.name ?? null,
            taxType: r?.taxType ?? null,
            taxDescription: r?.description ?? null,
            accountingCategoryCode: l.category || null,
            sequence: i + 1,
          };
        }),
    } as unknown as Invoice;
    return docSource.buildData(draft);
  }, [customer, billingAddress, currency, issueDate, dueDate, termsWording, po, message, subtotal, vatTotal, lines, rateById, mode, docSource]);

  const submitting = consumeSeq.isPending || generate.isPending;

  const raise = async () => {
    if (problems.length > 0 || !customer) {
      showToast({ type: 'error', message: problems[0] ?? 'Choose a customer.' });
      return;
    }
    try {
      const seq = await consumeSeq.mutateAsync({
        organisationId: orgId,
        type: mode === 'proforma' ? 'Proforma' : 'Invoice',
      });
      const created = await generate.mutateAsync({
        organisationId: orgId,
        number: seq.formatted,
        docType: mode === 'proforma' ? 'Proforma' : 'Adhoc',
        customerPartyId: customer.id,
        customerName: customer.name,
        customerCode: customer.code,
        billingName: customer.billingName,
        billingAddress,
        customerTaxNumber: customer.taxNumber?.trim() || null,
        customerTaxCountry: customer.taxSchemeCountryCode ?? null,
        currencyIsoCode: currency,
        issueDate: issueDate || null,
        dueDate: dueDate || null,
        paymentTerms: termsWording,
        poNumber: po.trim() || null,
        message: message.trim() || null,
        emailMessage:
          emailMessage.trim() || `Dear ${customer.name},\nPlease find attached your invoice.`,
        customerPaymentMethod: customer.paymentMethod ?? null,
        issueNow: mode === 'send',
        roundingMethod,
        lines: lines.map((l) => ({
          sourceChargeCostId: null,
          orderId: null,
          orderNumber: null,
          description: l.description.trim(),
          type: 'Custom',
          salesNominal: l.nominal,
          accountingCategoryCode: l.category || null,
          vatTreatmentKey: treatment?.ruleKey ?? null,
          net: l.net,
          taxRatePct: pctOf(l.rateId),
          taxRateId: l.rateId,
        })),
      });
      try {
        const template = docSource.templateFor(created.docType, customer);
        if (template) {
          const blob = await pdf(<DocumentPdf template={template} data={docSource.buildData(created)} />).toBlob();
          await tmsApi.uploadInvoicePdf(created.id, blob);
        }
      } catch {
        // ignore — recreated from the layout on download
      }
      const label = mode === 'send' ? 'raised & issued' : mode === 'draft' ? 'saved as draft' : 'saved';
      showToast({
        type: 'success',
        message: `${mode === 'proforma' ? 'Proforma' : 'Invoice'} ${created.number} ${label} for ${customer.name}.`,
      });
      onClose();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error && err.message ? err.message : 'Could not raise the invoice — please try again.',
      });
    }
  };

  const input =
    'rounded-sm border border-slate-300 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-purple-400';

  return (
    <div className="shrink-0 rounded-md border-2 border-purple-200 bg-purple-50/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold text-purple-900">
          Ad-hoc invoice — bills no order (storage, a one-off fee)
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded-sm p-1 text-slate-400 hover:bg-white hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_17rem] xl:grid-cols-[1fr_16rem_28rem]">
        {/* ── Customer + lines ── */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-end gap-2 rounded-md border border-slate-200 bg-white p-2">
            <label className="flex flex-col text-[11px] font-medium text-slate-500">
              Customer
              <input
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search…"
                className={cn(input, 'mt-0.5 w-40')}
              />
            </label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className={cn(input, 'min-w-[16rem] flex-1', !customerId && 'border-amber-300')}
            >
              <option value="">Choose a customer…</option>
              {sortedCustomers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.code ? ` (${c.code})` : ''}
                  {c.status !== 'Active' ? ` — ${c.status === 'CeasedTrading' ? 'ceased trading' : 'suspended'}` : ''}
                </option>
              ))}
            </select>
            {customer && (
              <span className="text-[11px] text-slate-500">
                {currency}
                {customer.taxNumber ? ` · VAT ${customer.taxNumber}` : ' · no VAT number'}
                {treatment && (
                  <span
                    className="ml-1 rounded-sm bg-slate-100 px-1 font-mono text-[10px] font-semibold text-slate-500"
                    title="The VAT treatment line the default VAT came from (no route: a home customer is taken as domestic)"
                  >
                    {vatTreatmentLineLabel(vatMatrix, treatment.ruleKey)}
                  </span>
                )}
              </span>
            )}
          </div>

          <div className="rounded-md border border-slate-200 bg-white">
            <ul className="divide-y divide-slate-100">
              {lines.map((l) => (
                <li key={l.key} className="flex flex-wrap items-center gap-2 px-2 py-1.5">
                  <input
                    value={l.description}
                    onChange={(e) => patch(l.key, { description: e.target.value })}
                    placeholder="Description…"
                    className={cn(input, 'min-w-0 flex-1', !l.description.trim() && 'border-amber-300')}
                  />
                  <select
                    aria-label="Sales nominal"
                    value={l.nominal}
                    onChange={(e) => patch(l.key, { nominal: e.target.value })}
                    className={cn(input, 'w-28', !l.nominal && 'border-amber-300 text-amber-700')}
                  >
                    <option value="">Nominal…</option>
                    {salesNominals.map((n) => (
                      <option key={n.id} value={n.code}>
                        {n.code}
                        {n.description ? ` — ${n.description}` : ''}
                      </option>
                    ))}
                  </select>
                  {accountingCategories && (
                    <select
                      aria-label="Accounting category"
                      value={l.category}
                      onChange={(e) => patch(l.key, { category: e.target.value })}
                      className={cn(input, 'w-28', !l.category && 'border-amber-300 text-amber-700')}
                    >
                      <option value="">Category…</option>
                      {accountingCategories.map((c) => (
                        <option key={c.id} value={c.code}>
                          {c.code} — {c.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <select
                    aria-label="VAT rate"
                    value={l.rateId}
                    onChange={(e) => {
                      setRateTouched((t) => new Set(t).add(l.key));
                      patch(l.key, { rateId: e.target.value });
                    }}
                    className={cn(input, 'w-36', !l.rateId && 'border-amber-300 text-amber-700')}
                  >
                    <option value="">VAT rate…</option>
                    {activeRates.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.taxPercentage}% · {r.name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    aria-label="Net"
                    value={l.net}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      patch(l.key, { net: Number.isFinite(n) ? round2(n) : 0 });
                    }}
                    className={cn(input, 'w-24 text-right tabular-nums')}
                  />
                  <span className="w-20 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-800">
                    {money(l.net * (1 + pctOf(l.rateId) / 100), currency)}
                  </span>
                  <button
                    type="button"
                    aria-label="Remove line"
                    onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                    className="text-slate-300 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, newLine()])}
              className="m-2 inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-50"
            >
              <Plus className="h-3 w-3" /> Add line
            </button>
          </div>

          {problems.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800">
              {problems.slice(0, 4).join(' · ')}
              {problems.length > 4 && ` · …${problems.length - 4} more`}
            </div>
          )}
        </div>

        {/* ── Settings + totals ── */}
        <div className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white p-3 text-[11px] text-slate-500">
          <label className="block font-medium">
            Issue date
            <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={cn(input, 'mt-0.5 w-full')} />
          </label>
          <label className="block font-medium">
            Due date
            <input
              type="date"
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                setDueDateEdited(true);
              }}
              className={cn(input, 'mt-0.5 w-full')}
            />
            <span className="mt-0.5 block text-[10px] font-normal text-slate-400">
              {dueDateEdited ? 'Set by hand' : termsWording}
            </span>
          </label>
          <label className="block font-medium">
            PO number
            <input value={po} onChange={(e) => setPo(e.target.value)} className={cn(input, 'mt-0.5 w-full')} />
          </label>
          <label className="block font-medium">
            Email message
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={2}
              placeholder={customer ? `Dear ${customer.name}, …` : ''}
              className={cn(input, 'mt-0.5 w-full resize-none')}
            />
          </label>
          <label className="block font-medium">
            Invoice message
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className={cn(input, 'mt-0.5 w-full resize-none')}
            />
          </label>
          <div className="space-y-0.5 text-slate-600">
            {(
              [
                ['send', 'Generate & issue now'],
                ['draft', 'Generate as draft'],
                ['proforma', 'Save as proforma'],
              ] as const
            ).map(([value, label]) => (
              <label key={value} className="flex items-center gap-1.5">
                <input type="radio" name="adhoc-mode" checked={mode === value} onChange={() => setMode(value)} />
                {label}
              </label>
            ))}
          </div>
          <ForeignVatNotice currency={currency} orgCurrency={organisation?.currencyIsoCode} vatTotal={vatTotal} />
          <div className="mt-1 border-t border-slate-200 pt-2 text-xs">
            <Row label="Subtotal" value={money(subtotal, currency)} />
            <Row label="VAT" value={money(vatTotal, currency)} />
            <Row label="Total" value={money(subtotal + vatTotal, currency)} strong />
          </div>
          <button
            type="button"
            onClick={() => void raise()}
            disabled={submitting || problems.length > 0}
            title={problems[0]}
            className="inline-flex items-center justify-center gap-1 rounded-md bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            {mode === 'proforma' ? 'Save proforma' : 'Raise invoice'}
          </button>
        </div>

        {/* ── Preview ── */}
        <div className="hidden xl:block">
          {previewTemplate ? (
            <PdfPreviewPane template={previewTemplate} data={previewData} className="h-[36rem]" />
          ) : (
            <div className="rounded-md border border-dashed border-slate-300 bg-white p-3 text-[11px] text-slate-500">
              No document layout is set as the default for invoices, so there is nothing to preview.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className={cn('flex justify-between py-0.5', strong ? 'font-semibold text-slate-800' : 'text-slate-600')}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function money(v: number, iso: string | null | undefined): string {
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency: iso || 'EUR' }).format(v);
  } catch {
    return v.toFixed(2);
  }
}
